import React, { Component } from "react";
import EventEmitter from "../../../services/event-emitter";
import { getRequest, postRequest } from "../../../api/ApiService";
import { logError } from "../../../services/utils";
import { Modal, Button } from "react-bootstrap";
import UiButton from "../decisions/ui-button";
import HelpIconButtonWithTooltipAndTypeformModal from "../../typeform/HelpIconButtonWithTooltipAndTypeformModal";
import NumberFormat from "react-number-format";
import _ from "lodash";
import TypeformContent from "../../typeform/TypeformContent";

class NetPresentValueAnalysis extends Component {
  constructor(props) {
    super(props);

    this.state = {
      machine_purchases: [0, 0, 0, 0],
      net_incoming_cash_flows: [0, 0, 0, 0],
      discount_rate: 0.15,
      gross_present_value: null,
      net_present_value: null,
      real_rate_of_return: null,
      irr: null,
      show: false
    };

    this.eventemitter = EventEmitter.getInstance();
  }

  componentDidMount() {
    this.eventemitter.on("show-npv-modal", this.show);
  }

  componentWillUnmount() {
    this.eventemitter.off("show-npv-modal", this.show);
  }

  show = () => {
    this.setState(
      {
        show: true
      },
      () => {
        this.fetchData();
      }
    );
  };

  hide = () => {
    this.setState(
      {
        show: false
      },
      () => {
        this.eventemitter.trigger("show-buying-machines-modal");
      }
    );
  };

  getCompetitionMetricsRequest = () => {
    const { currentRound } = this.props;
    const round_number = currentRound - 1;

    if (round_number < 1) {
      return null;
    }

    return getRequest(
      `${process.env.REACT_APP_API_URL}competition/metrics/?round_number=${round_number}`
    );
  };

  fetchData = () => {
    const joinRequest = this.getCompetitionMetricsRequest();

    if (!joinRequest) {
      return;
    }

    joinRequest
      .then(res => {
        const wacc_metric = res.body.find(i => i.name === "WACC");

        if (wacc_metric) {
          this.setState({
            discount_rate: wacc_metric.value
          });
        }
      })
      .catch(err => {
        logError(err, "NetPresentValueAnalysis", "fetchData");
      });
  };

  updateMachinePurchases = (e, value) => {
    const index = Number(e.target.dataset.index);
    const machine_purchases = _.cloneDeep(this.state.machine_purchases);
    machine_purchases[index] = value
      ? typeof value === "object"
        ? value.value
        : value
      : 0;

    this.setState({
      machine_purchases
    });
  };

  updateNetIncomingCashFlows = (e, value) => {
    const index = Number(e.target.dataset.index);
    const net_incoming_cash_flows = _.cloneDeep(
      this.state.net_incoming_cash_flows
    );
    net_incoming_cash_flows[index] = value
      ? typeof value === "object"
        ? value.value
        : value
      : 0;

    this.setState({
      net_incoming_cash_flows
    });
  };

  get discountRate() {
    const { discount_rate } = this.state;

    return discount_rate * 100;
  }

  updateDiscountRate = (e, value) => {
    const discount_rate = (value > 100 ? 100 : value) / 100;

    this.setState({
      discount_rate
    });
  };

  get isDiscountRateDisabled() {
    const { discount_rate } = this.state;

    return discount_rate !== null;
  }

  get realRateOfReturn() {
    const { real_rate_of_return } = this.state;

    return real_rate_of_return !== null ? real_rate_of_return * 100 : null;
  }

  get irr() {
    const { irr } = this.state;

    return irr !== null ? irr * 100 : null;
  }

  calculateRequest = () => {
    const {
      machine_purchases,
      net_incoming_cash_flows,
      discount_rate
    } = this.state;

    const data = {
      machine_purchases,
      net_incoming_cash_flows,
      discount_rate
    };

    return postRequest(
      `${process.env.REACT_APP_API_URL}team/net_present_value/`
    ).send(data);
  };

  calculate = () => {
    this.calculateRequest()
      .then(res => {
        const {
          gross_present_value,
          irr,
          net_present_value,
          real_rate_of_return
        } = res.body;

        this.setState({
          gross_present_value,
          irr,
          net_present_value,
          real_rate_of_return
        });
      })
      .catch(err => {
        logError(err, "NetPresentValueAnalysis", "calculate");
      });
  };

  render() {
    const {
      show,
      machine_purchases,
      net_incoming_cash_flows,
      gross_present_value,
      net_present_value
    } = this.state;

    return (
      <Modal
        show={show}
        onHide={this.hide}
        dialogClassName="modal-dialog-bigger-more"
      >
        <Modal.Header>
          <Modal.Title>
            NPV Analysis
            <HelpIconButtonWithTooltipAndTypeformModal
              {...TypeformContent.NPV_ANALYSIS}
            />
            <UiButton
              className="npv-analysis-button calculate"
              onClick={this.calculate}
            >
              Calculate
            </UiButton>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive mt-15">
            <table className="table">
              <thead>
                <tr className="border-none">
                  <th />
                  <th className="text-center">
                    <b className="title-black">Cash Flows Period 0</b>
                  </th>
                  <th className="text-center">
                    <b className="title-black">Cash Flows Period 1</b>
                  </th>
                  <th className="text-center">
                    <b className="title-black">Cash Flows Period 2</b>
                  </th>
                  <th className="text-center">
                    <b className="title-black">Cash Flows Period 3</b>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-none">
                  <td>Machine Purchases</td>
                  <td>
                    <NumberFormat
                      name="machine_purchases_period_0"
                      className="machine-purchases-input"
                      allowNegative={false}
                      data-index={0}
                      prefix="-$"
                      value={machine_purchases[0]}
                      onChange={this.updateMachinePurchases}
                      thousandSeparator=","
                    />
                  </td>
                  <td>
                    <NumberFormat
                      name="machine_purchases_period_1"
                      className="machine-purchases-input"
                      allowNegative={false}
                      data-index={1}
                      prefix="-$"
                      value={machine_purchases[1]}
                      onChange={this.updateMachinePurchases}
                      thousandSeparator=","
                    />
                  </td>
                  <td>
                    <NumberFormat
                      name="machine_purchases_period_2"
                      className="machine-purchases-input"
                      allowNegative={false}
                      data-index={2}
                      prefix="-$"
                      value={machine_purchases[2]}
                      onChange={this.updateMachinePurchases}
                      thousandSeparator=","
                    />
                  </td>
                  <td>
                    <NumberFormat
                      name="machine_purchases_period_3"
                      className="machine-purchases-input"
                      allowNegative={false}
                      data-index={3}
                      prefix="-$"
                      value={machine_purchases[3]}
                      onChange={this.updateMachinePurchases}
                      thousandSeparator=","
                    />
                  </td>
                </tr>
                <tr className="border-none">
                  <td>Expected Operating Cash Flows</td>
                  <td>
                    <NumberFormat
                      name="net_incoming_cash_flows_period_0"
                      allowNegative={false}
                      data-index={0}
                      prefix="$"
                      value={net_incoming_cash_flows[0]}
                      onChange={this.updateNetIncomingCashFlows}
                      thousandSeparator=","
                    />
                  </td>
                  <td>
                    <NumberFormat
                      name="net_incoming_cash_flows_period_1"
                      allowNegative={false}
                      data-index={1}
                      prefix="$"
                      value={net_incoming_cash_flows[1]}
                      onChange={this.updateNetIncomingCashFlows}
                      thousandSeparator=","
                    />
                  </td>
                  <td>
                    <NumberFormat
                      name="net_incoming_cash_flows_period_2"
                      allowNegative={false}
                      data-index={2}
                      prefix="$"
                      value={net_incoming_cash_flows[2]}
                      onChange={this.updateNetIncomingCashFlows}
                      thousandSeparator=","
                    />
                  </td>
                  <td>
                    <NumberFormat
                      name="net_incoming_cash_flows_period_3"
                      allowNegative={false}
                      data-index={3}
                      prefix="$"
                      value={net_incoming_cash_flows[3]}
                      onChange={this.updateNetIncomingCashFlows}
                      thousandSeparator=","
                    />
                  </td>
                </tr>
                <tr className="border-none">
                  <td>
                    Discount Rate
                    <HelpIconButtonWithTooltipAndTypeformModal
                      {...TypeformContent.DISCOUNT_RATE}
                    />
                  </td>
                  <td colSpan="4">
                    <NumberFormat
                      name="discount_rate"
                      allowNegative={false}
                      suffix="%"
                      value={this.discountRate}
                      onChange={this.updateDiscountRate}
                      decimalPrecision={2}
                      disabled={this.isDiscountRateDisabled}
                    />
                  </td>
                </tr>
                <tr className="border-none">
                  <td>
                    <b>Present Value</b>
                    <HelpIconButtonWithTooltipAndTypeformModal
                      {...TypeformContent.TIME_VALUE_OF_MONEY}
                    />
                  </td>
                  <td colSpan="4">
                    <NumberFormat
                      name="present_value"
                      prefix="$"
                      value={gross_present_value}
                      thousandSeparator=","
                      decimalPrecision={2}
                      displayType="text"
                    />
                  </td>
                </tr>
                <tr className="border-none">
                  <td>
                    <b>Net Present Value</b>
                    <HelpIconButtonWithTooltipAndTypeformModal
                      {...TypeformContent.NPV_ANALYSIS}
                    />
                  </td>
                  <td colSpan="4">
                    <NumberFormat
                      name="net_present_value"
                      prefix="$"
                      value={net_present_value}
                      thousandSeparator=","
                      decimalPrecision={2}
                      displayType="text"
                    />
                  </td>
                </tr>
                <tr className="border-none">
                  <td>
                    <b>IRR</b>
                    <HelpIconButtonWithTooltipAndTypeformModal
                      {...TypeformContent.IRR}
                    />
                  </td>
                  <td colSpan="4">
                    <NumberFormat
                      name="irr"
                      suffix="%"
                      value={this.irr}
                      decimalPrecision={2}
                      displayType="text"
                    />
                  </td>
                </tr>
                <tr className="border-none">
                  <td>
                    <b>Real Rate of Return</b>
                  </td>
                  <td colSpan="4">
                    <NumberFormat
                      name="real_rate_of_return"
                      suffix="%"
                      value={this.realRateOfReturn}
                      decimalPrecision={2}
                      displayType="text"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer bsClass="modal-footer op-decision-modal-footer">
          <Button bsStyle="save" onClick={this.hide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default NetPresentValueAnalysis;



// WEBPACK FOOTER //
// ./src/components/operations/helper-components/net-present-value-analysis.js